import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AUDIT } from "@/constants/testIds";
import { toast } from "sonner";
import {
  ScrollText, Bot, User, Cpu, X, Copy, ExternalLink,
} from "lucide-react";
import ExportButton from "@/components/ExportButton";
import SearchBar, { useLocalFilter } from "@/components/SearchBar";

const ACTIONS = [
  "user_login", "user_created", "user_deleted",
  "trial_user_synced", "trial_user_updated",
  "message_sent_ai", "message_sent_human", "message_received",
  "cadence_updated", "guardrails_updated", "kb_updated", "template_updated",
  "whatsapp_number_updated", "escalation_triggered", "digest_generated",
];

const roleIcon = (role) => {
  if (role === "ai") return <Bot className="h-3 w-3 text-indigo-500" />;
  if (role === "system") return <Cpu className="h-3 w-3 text-slate-400" />;
  return <User className="h-3 w-3 text-slate-500" />;
};

const copyToClipboard = (v, label = "Copied") => {
  if (!v) return;
  navigator.clipboard.writeText(String(v));
  toast.success(`${label}: ${String(v).slice(0, 40)}${String(v).length > 40 ? "…" : ""}`);
};

function IdRow({ label, value, testId }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <span className="text-[10px] uppercase tracking-widest text-slate-500 min-w-[8rem]">{label}</span>
      <span className="flex-1 font-mono text-[11px] text-slate-800 truncate" title={value || ""} data-testid={testId}>
        {value || "—"}
      </span>
      {value && (
        <button
          onClick={() => copyToClipboard(value, label)}
          className="p-1 hover:bg-slate-200 rounded"
          title={`Copy ${label}`}
          data-testid={testId ? `${testId}-copy` : undefined}
        >
          <Copy className="h-3 w-3 text-slate-500" />
        </button>
      )}
    </div>
  );
}

function AuditDetailDrawer({ logId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/audit-logs/${logId}`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        toast.error("Could not load audit detail");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [logId]);

  const detailsJson = useMemo(
    () => (data?.log?.details ? JSON.stringify(data.log.details, null, 2) : ""),
    [data],
  );

  const isUserTarget = data?.target?.type === "trial_user" || data?.target?.type === "user";

  // Try to surface the AI model/agent used, when the actor is AI.
  const aiSourceHint = useMemo(() => {
    if (data?.actor?.role !== "ai") return null;
    const d = data?.log?.details || {};
    const parts = [];
    if (d.model) parts.push(`model=${d.model}`);
    if (d.agent) parts.push(`agent=${d.agent}`);
    if (d.provider) parts.push(`provider=${d.provider}`);
    if (d.trigger) parts.push(`trigger=${d.trigger}`);
    return parts.length ? parts.join(" · ") : null;
  }, [data]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/30"
      onClick={onClose}
      data-testid="audit-drawer"
    >
      <div
        className="absolute right-0 top-0 h-full w-full max-w-2xl bg-white shadow-2xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Audit Entry</div>
            <div className="text-sm font-semibold text-slate-800 font-mono">{logId?.slice(0, 8)}…</div>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-slate-100 rounded"
            data-testid="audit-drawer-close"
            title="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading && <div className="p-6 text-sm text-slate-400">Loading…</div>}
        {!loading && data && (
          <div className="p-4 space-y-5">
            {/* When + Action */}
            <section>
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 mb-2">When</div>
              <div className="text-sm text-slate-800 tabular">
                {new Date(data.log.created_at).toLocaleString(undefined, {
                  weekday: "short", year: "numeric", month: "short", day: "numeric",
                  hour: "2-digit", minute: "2-digit", second: "2-digit",
                })}
              </div>
              <div className="mt-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 mb-1">Action</div>
              <div className="font-mono text-sm text-orange-600" data-testid="audit-drawer-action">
                {data.log.action}
              </div>
            </section>

            {/* Actor */}
            <section>
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 mb-2 flex items-center gap-2">
                Actor {roleIcon(data.actor?.role)}
                <span className="text-[9px] font-bold text-slate-400 uppercase">{data.actor?.role || "—"}</span>
              </div>
              <div className="rounded border border-slate-200 bg-slate-50 p-3">
                <IdRow label="Name" value={data.actor?.name} testId="audit-actor-name" />
                <IdRow label="Actor ID" value={data.actor?.id} testId="audit-actor-id" />
                {data.actor?.email && <IdRow label="Email" value={data.actor.email} testId="audit-actor-email" />}
                {aiSourceHint && (
                  <div className="mt-2 text-[11px] text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-2 py-1 font-mono">
                    {aiSourceHint}
                  </div>
                )}
              </div>
            </section>

            {/* Target */}
            <section>
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 mb-2">
                Target {data.target?.type ? <span className="ml-1 text-[9px] font-bold text-slate-400 uppercase">{data.target.type}</span> : null}
              </div>
              {!data.target?.id && <div className="text-xs text-slate-500 italic">No target for this action.</div>}
              {data.target?.id && (
                <div className="rounded border border-slate-200 bg-slate-50 p-3 space-y-1">
                  {data.target?.name && <IdRow label="Name" value={data.target.name} testId="audit-target-name" />}
                  <IdRow label="Connect ID" value={data.target.id} testId="audit-target-connect-id" />
                  {isUserTarget && (
                    <>
                      <IdRow label="Master DB ID" value={data.target.external_id} testId="audit-target-external-id" />
                      <IdRow label="Subscription ID" value={data.target.external_subscription_id} testId="audit-target-ext-sub-id" />
                      {data.target.email && <IdRow label="Email" value={data.target.email} testId="audit-target-email" />}
                      {data.target.phone && <IdRow label="Phone / WA" value={data.target.phone} testId="audit-target-phone" />}
                      <Link
                        to={`/users/${data.target.id}`}
                        className="mt-2 inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline"
                        data-testid="audit-target-open-profile"
                      >
                        <ExternalLink className="h-3 w-3" /> Open user profile
                      </Link>
                    </>
                  )}
                </div>
              )}
            </section>

            {/* Details */}
            <section>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Details</div>
                {detailsJson && (
                  <button
                    onClick={() => copyToClipboard(detailsJson, "JSON")}
                    className="inline-flex items-center gap-1 text-[10px] uppercase font-semibold tracking-widest text-slate-500 hover:text-slate-800"
                    data-testid="audit-drawer-copy-json"
                  >
                    <Copy className="h-3 w-3" /> Copy JSON
                  </button>
                )}
              </div>
              <pre
                className="rounded border border-slate-200 bg-slate-900 text-slate-100 p-3 text-[11px] font-mono overflow-x-auto max-h-[300px]"
                data-testid="audit-drawer-details-json"
              >
                {detailsJson || "{ }"}
              </pre>
            </section>

            {/* Related events */}
            <section>
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 mb-2">
                Related Events {data.related_events?.length ? `· last ${data.related_events.length}` : ""}
              </div>
              {(!data.related_events || data.related_events.length === 0) && (
                <div className="text-xs text-slate-500 italic">No other audit entries for this target.</div>
              )}
              {data.related_events && data.related_events.length > 0 && (
                <div className="rounded border border-slate-200 divide-y divide-slate-100" data-testid="audit-drawer-related">
                  {data.related_events.map((r) => (
                    <div key={r.id} className="flex items-center gap-3 px-3 py-2 text-xs">
                      <span className="text-[10px] tabular text-slate-500 whitespace-nowrap">
                        {new Date(r.created_at).toLocaleString(undefined, {
                          month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                        })}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        {roleIcon(r.actor_role)}
                        <span className="font-medium">{r.actor_display_name}</span>
                      </span>
                      <span className="font-mono text-orange-600 truncate">{r.action}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [action, setAction] = useState("");
  const [q, setQ] = useState("");
  const [openLogId, setOpenLogId] = useState(null);
  const filtered = useLocalFilter(logs, q, [
    "action", "actor_display_name", "target_display_name",
    "actor", "target_id", "target_type",
    "target_external_id", "target_external_subscription_id",
  ]);

  const load = async () => {
    const p = action ? `?action=${action}` : "";
    setLogs((await api.get(`/audit-logs${p}`)).data);
  };
  useEffect(() => { load(); }, [action]);

  return (
    <div data-testid={AUDIT.root} className="p-8 lg:p-12 max-w-[1300px]">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500 mb-1">Governance</div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tighter flex items-center gap-2">
            <ScrollText className="h-7 w-7 text-orange-600" /> Audit Logs
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Every AI action, every admin change. Click any row for full context.
          </p>
        </div>
        <ExportButton
          path={`/exports/audit-logs.xlsx${action ? `?action=${action}` : ""}`}
          filename="audit_logs.xlsx"
          testId="audit-export-btn"
        />
      </div>

      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <SearchBar value={q} onChange={setQ} placeholder="Search by actor, target name, action, or ID…" testId="audit-search" />
        <select
          data-testid={AUDIT.actionFilter}
          value={action}
          onChange={(e) => setAction(e.target.value)}
          className="px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none"
        >
          <option value="">All actions</option>
          {ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <div className="text-xs text-slate-500 tabular">{logs.length} entries</div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table data-testid={AUDIT.table} className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Time</th>
              <th className="text-left px-4 py-3 font-semibold">Actor</th>
              <th className="text-left px-4 py-3 font-semibold">Action</th>
              <th className="text-left px-4 py-3 font-semibold">Target</th>
              <th className="text-left px-4 py-3 font-semibold">Details</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((l) => (
              <tr
                key={l.id}
                data-testid={`audit-row-${l.id}`}
                onClick={() => setOpenLogId(l.id)}
                className="border-b border-slate-100 last:border-0 hover:bg-orange-50/50 cursor-pointer transition-colors duration-150"
                title="Click for full detail"
              >
                <td className="px-4 py-2 text-xs tabular text-slate-600 whitespace-nowrap">
                  {new Date(l.created_at).toLocaleString()}
                </td>
                <td className="px-4 py-2">
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold">
                    {roleIcon(l.actor_role)}
                    {l.actor_display_name || (l.actor === "ai" ? "AI" : (l.actor || "").slice(0, 8))}
                  </span>
                </td>
                <td className="px-4 py-2 font-mono text-xs text-orange-600">{l.action}</td>
                <td className="px-4 py-2 text-xs text-slate-700">
                  {l.target_id
                    ? (l.target_display_name || <span className="font-mono text-slate-500">{`${l.target_type || "?"}:${l.target_id.slice(0, 8)}…`}</span>)
                    : "—"}
                </td>
                <td className="px-4 py-2 text-xs text-slate-500 font-mono max-w-md truncate">
                  {Object.keys(l.details || {}).length > 0 ? JSON.stringify(l.details) : "—"}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-sm text-slate-500">No logs match.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {openLogId && <AuditDetailDrawer logId={openLogId} onClose={() => setOpenLogId(null)} />}
    </div>
  );
}
